import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import '@/App.css';
import LoginPage from './pages/LoginPage';
import FullFunnel from './pages/FullFunnel';
import AdminPanel from './pages/AdminPanel';
import ControlPanel from './pages/ControlPanel';
import UploadCenter from './pages/UploadCenter';
import FunnelMapping from './pages/FunnelMapping';
import AdProperties from './pages/AdProperties';
import AdsSuperset from './pages/AdsSuperset';
import SearchTrends from './pages/SearchTrends';
import ExplorationReport from './pages/ExplorationReport';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// === Auth fallback: localStorage-backed Bearer token ===
// Some browsers / proxies silently drop HttpOnly Set-Cookie headers (esp. with
// SameSite=None). When that happens, subsequent /api/* calls return 401 even
// though the user just authenticated — producing a login redirect loop.
// Solution: store the session_token in localStorage during AuthCallback and
// monkey-patch window.fetch ONCE to auto-attach an `Authorization: Bearer ...`
// header on every same-origin /api/* request. The backend's get_current_user()
// already supports both cookie and Bearer token, so this acts as a safety net
// without changing any other component.
const TOKEN_KEY = 'trace_session_token';
(function patchFetchOnce() {
  if (typeof window === 'undefined' || window.__traceFetchPatched) return;
  window.__traceFetchPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.includes('/api/')) {
        const token = window.localStorage.getItem(TOKEN_KEY);
        if (token) {
          const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
          if (!headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
          }
          init = { ...init, headers };
        }
      }
    } catch (_) { /* no-op */ }
    return originalFetch(input, init);
  };
})();

// Global helper: any <td>/<th> whose text content overflows its cell gets
// a `title` attribute equal to its text — so users see the full value as a
// native browser tooltip on hover. Runs on initial render + on every DOM
// mutation (filters, sort, drill-downs).
function useGlobalCellTitleInjector() {
  useEffect(() => {
    const decorate = () => {
      const cells = document.querySelectorAll('td, th');
      cells.forEach(cell => {
        // Skip cells with interactive children — avoid breaking native tooltips
        if (cell.querySelector('button, input, [role="tooltip"], svg[role="img"]')) return;
        const overflowing = cell.scrollWidth > cell.clientWidth + 1;
        const text = (cell.textContent || '').trim();
        if (overflowing && text && !cell.getAttribute('title')) {
          cell.setAttribute('title', text);
        } else if (!overflowing && cell.getAttribute('title') === text) {
          // Cell expanded — clear our injected title
          cell.removeAttribute('title');
        }
      });
    };
    decorate();
    const obs = new MutationObserver(() => {
      // Throttle: only re-scan after the current frame
      window.requestAnimationFrame(decorate);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, []);
}

// Auth Callback Component
function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/login');
      return;
    }

    // Exchange session_id for user data
    fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to authenticate');
        return res.json();
      })
      .then(user => {
        // Persist token to localStorage as a Bearer-auth fallback in case the
        // HttpOnly cookie is stripped by a proxy / blocked by the browser.
        if (user && user.session_token) {
          try { window.localStorage.setItem(TOKEN_KEY, user.session_token); } catch (_) { /* ignore */ }
        }
        // Navigate to dashboard with user data
        navigate('/funnel', { replace: true, state: { user } });
      })
      .catch(err => {
        console.error('Auth error:', err);
        navigate('/login');
      });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-600">Authenticating...</div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // Otherwise, verify session
    fetch(`${API}/auth/me`, {
      credentials: 'include'
    })
      .then(res => {
        if (!res.ok) throw new Error('Not authenticated');
        return res.json();
      })
      .then(userData => {
        setUser(userData);
        setIsAuthenticated(true);
      })
      .catch(() => {
        // Clear any stale token before redirecting to login.
        try { window.localStorage.removeItem(TOKEN_KEY); } catch (_) { /* ignore */ }
        setIsAuthenticated(false);
        navigate('/login');
      });
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return React.cloneElement(children, { user });
}

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment (not query params)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<Navigate to="/funnel" replace />} />
      <Route path="/funnel" element={
        <ProtectedRoute>
          <FullFunnel />
        </ProtectedRoute>
      } />
      <Route path="/ad-properties" element={
        <ProtectedRoute>
          <AdProperties />
        </ProtectedRoute>
      } />
      <Route path="/exploration" element={
        <ProtectedRoute>
          <ExplorationReport />
        </ProtectedRoute>
      } />
      <Route path="/search-trends" element={
        <ProtectedRoute>
          <SearchTrends />
        </ProtectedRoute>
      } />
      <Route path="/ads-superset" element={
        <ProtectedRoute>
          <AdsSuperset />
        </ProtectedRoute>
      } />
      <Route path="/upload" element={
        <ProtectedRoute>
          <UploadCenter />
        </ProtectedRoute>
      } />
      <Route path="/funnel-mapping" element={
        <ProtectedRoute>
          <FunnelMapping />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      <Route path="/control-panel" element={
        <ProtectedRoute>
          <ControlPanel />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/funnel" replace />} />
    </Routes>
  );
}

function App() {
  useGlobalCellTitleInjector();
  return (
    <div className="App">
      <Router>
        <AppRouter />
      </Router>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
